// Pure geometry for swept projectile collision and area tests.
// Phaser-free so it can be unit-tested in Node (tests/combat-geom.test.ts).

const EPS = 1e-9;
export const TAU = Math.PI * 2;

/** Wrap an angle to [-π, π). */
export function wrapAngle(a: number): number {
  let r = (a + Math.PI) % TAU;
  if (r < 0) r += TAU;
  return r - Math.PI;
}

/** Absolute smallest difference between two angles (0..π). */
export function angleDiff(a: number, b: number): number {
  return Math.abs(wrapAngle(a - b));
}

/**
 * Swept segment vs axis-aligned box (slab method).
 * The segment is p(t) = (x0 + dx·t, y0 + dy·t), t ∈ [0, 1].
 * Returns the entry t (0 if the start point is already inside), or -1 when it misses.
 */
export function segmentVsAabb(
  x0: number,
  y0: number,
  dx: number,
  dy: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): number {
  let tMin = 0;
  let tMax = 1;
  if (dx > -EPS && dx < EPS) {
    if (x0 < minX || x0 > maxX) return -1;
  } else {
    const inv = 1 / dx;
    let t1 = (minX - x0) * inv;
    let t2 = (maxX - x0) * inv;
    if (t1 > t2) {
      const s = t1;
      t1 = t2;
      t2 = s;
    }
    if (t1 > tMin) tMin = t1;
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return -1;
  }
  if (dy > -EPS && dy < EPS) {
    if (y0 < minY || y0 > maxY) return -1;
  } else {
    const inv = 1 / dy;
    let t1 = (minY - y0) * inv;
    let t2 = (maxY - y0) * inv;
    if (t1 > t2) {
      const s = t1;
      t1 = t2;
      t2 = s;
    }
    if (t1 > tMin) tMin = t1;
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax) return -1;
  }
  return tMin;
}

/**
 * Swept segment vs circle. Returns the entry t in [0, 1] (0 if the start is inside), or -1.
 */
export function segmentVsCircle(x0: number, y0: number, dx: number, dy: number, cx: number, cy: number, r: number): number {
  const fx = x0 - cx;
  const fy = y0 - cy;
  const c = fx * fx + fy * fy - r * r;
  if (c <= 0) return 0;
  const a = dx * dx + dy * dy;
  if (a < EPS) return -1;
  const b = 2 * (fx * dx + fy * dy);
  if (b >= 0) return -1; // moving away from the centre
  const disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  return t >= 0 && t <= 1 ? t : -1;
}

/** Squared distance from a point to the segment (x0,y0)-(x1,y1). */
export function distToSegmentSq(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const vx = x1 - x0;
  const vy = y1 - y0;
  const len2 = vx * vx + vy * vy;
  let t = len2 > EPS ? ((px - x0) * vx + (py - y0) * vy) / len2 : 0;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const qx = x0 + vx * t - px;
  const qy = y0 + vy * t - py;
  return qx * qx + qy * qy;
}

/** Distance from a point to an axis-aligned box (0 when inside). */
export function distToAabb(px: number, py: number, minX: number, minY: number, maxX: number, maxY: number): number {
  const dx = px < minX ? minX - px : px > maxX ? px - maxX : 0;
  const dy = py < minY ? minY - py : py > maxY ? py - maxY : 0;
  return Math.sqrt(dx * dx + dy * dy);
}
